/**!*******************************************************************
 *
 * Copyright (c) Epic AI GmbH. All rights reserved.
 * Author:  Alexander Claas
 *
 ********************************************************************!*/

import { fetchCloudRun } from "../Helper";
import { IGroupResult, IUserResult } from "@chatcaptain/types/organization";
import { useStore } from "@/store";
import { UserStore } from "@/store/constants";

let organizationId;
export const getOrganizationId = (): string | undefined => {
  const store = useStore();
  organizationId = store.getters[UserStore.Getters.ORGANIZATION_ID];
  return organizationId;
};

/**
 * Get all users in the current organization
 */
export const getAllGroups = async (): Promise<IGroupResult[] | null> => {
  if (!organizationId) getOrganizationId();
  const groups = await fetchCloudRun(
    process.env.VUE_APP_BASE_URL +
      "/admin/organizations/" +
      organizationId +
      "/groups",
    "GET"
  );
  return groups.groups as IGroupResult[];
};

/**
 * Gets a specified group
 * @param groupId the id of the group
 */
export const getGroup = async (
  groupId: string
): Promise<IGroupResult | null> => {
  if (!organizationId) getOrganizationId();
  const group = await fetchCloudRun(
    process.env.VUE_APP_BASE_URL +
      "/admin/organizations/" +
      organizationId +
      "/groups/" +
      groupId,
    "GET"
  );
  return group as IGroupResult;
};

export const updateGroup = async (groupId: string, permissions: string[]) => {
  if (!organizationId) getOrganizationId();
  await fetchCloudRun(
    process.env.VUE_APP_BASE_URL +
      "/admin/organizations/" +
      organizationId +
      "/groups/" +
      groupId,
    "PUT",
    {},
    {
      permissions,
    }
  );
};

/**
 * Delete a group
 * @param groupId the id of the group
 */
export const deleteGroup = async (groupId: string) => {
  if (!organizationId) return;
  await fetchCloudRun(
    process.env.VUE_APP_BASE_URL +
      "/admin/organizations/" +
      organizationId +
      "/groups/" +
      groupId,
    "DELETE"
  );
};

/**
 * Create a new group in the current organization
 * @param name of the new group
 * @param description of the new group (optional)
 */
export const createGroup = async (
  name: string,
  description?: string
): Promise<ICreateNewGroupResult> => {
  const result = await fetchCloudRun(
    process.env.VUE_APP_BASE_URL +
      "/admin/organizations/" +
      organizationId +
      "/groups",
    "POST",
    {},
    {
      name,
      description,
    }
  );
  return result as ICreateNewGroupResult;
};
interface ICreateNewGroupResult {
  id: string;
  name: string;
  description?: string;
}

export const getMembers = async (groupId: string): Promise<IUserResult[]> => {
  const response = await fetchCloudRun(
    process.env.VUE_APP_BASE_URL +
      "/admin/organizations/" +
      organizationId +
      "/groups/" +
      groupId +
      "/members",
    "GET"
  );
  return response.members as IUserResult[];
};

/**
 * Adds the specified user to the specified group
 * @param groupId the id of the group
 * @param userId the id of the user
 */
export const addMember = async (groupId: string, userId: string) => {
  await fetchCloudRun(
    process.env.VUE_APP_BASE_URL +
      "/admin/organizations/" +
      organizationId +
      "/groups/" +
      groupId +
      "/members",
    "POST",
    {},
    {
      uid: userId,
    }
  );
};

/**
 * Removes the specified user from the specified group
 * @param groupId the id of the group
 * @param userId the id of the user
 */
export const removeMember = async (groupId: string, userId: string) => {
  await fetchCloudRun(
    process.env.VUE_APP_BASE_URL +
      "/admin/organizations/" +
      organizationId +
      "/groups/" +
      groupId +
      "/members/" +
      userId,
    "DELETE",
    {}
  );
};
